import { ReactNode } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { C100HistoryChanges } from '../../../_types/carnivore100.type'
import { c100Tokens, C100Tokens } from '../c100-tokens'
import { fmtPublishDate } from '../../../pages/carnivore100/carnivore100.history.util'

const MONO = "'IBM Plex Mono', monospace"

interface Props {
  changes: C100HistoryChanges | null // null on the first published list
  previousDate: string | null
  onTicker: (ticker: string) => void
}

// What a reader checks first each week: who came on, who fell off, who moved.
export default function ChangesPanel({ changes, previousDate, onTicker }: Props) {
  const theme = useTheme()
  const t = c100Tokens(theme)

  const card = {
    bgcolor: t.card,
    border: `1px solid ${t.hairline}`,
    borderRadius: 2,
    p: 2,
    mb: 2
  }

  if (!changes || !previousDate) {
    return (
      <Box sx={card}>
        <Typography sx={{ fontFamily: MONO, fontSize: 12, color: t.muted }}>
          The first published list. There's no earlier list to compare it with.
        </Typography>
      </Box>
    )
  }

  const { added, dropped, movers } = changes

  return (
    <Box sx={card}>
      <Typography
        component="h2"
        sx={{
          fontFamily: MONO,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: t.text,
          mb: 1.5
        }}
      >
        Changes vs {fmtPublishDate(previousDate)}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: 2
        }}
      >
        <Group title={`Added · ${added.length}`} t={t} empty="No new names">
          {added.map(a => (
            <TickerChip key={a.ticker} ticker={a.ticker} name={a.name} onClick={onTicker} t={t}
              meta={`#${a.rank}`} color={t.gain} />
          ))}
        </Group>
        <Group title={`Dropped · ${dropped.length}`} t={t} empty="Nothing dropped">
          {dropped.map(d => (
            <TickerChip key={d.ticker} ticker={d.ticker} name={d.name} onClick={onTicker} t={t}
              meta={`was #${d.lastRank}`} color={t.loss} />
          ))}
        </Group>
        <Group title="Biggest movers" t={t} empty="No rank changes">
          {movers.map(m => (
            <TickerChip key={m.ticker} ticker={m.ticker} name={m.name} onClick={onTicker} t={t}
              meta={`${m.rankDelta > 0 ? '▲' : '▼'}${Math.abs(m.rankDelta)} → #${m.rank}`}
              color={m.rankDelta > 0 ? t.gain : t.loss} />
          ))}
        </Group>
      </Box>
    </Box>
  )
}

function Group({ title, empty, t, children }: {
  title: string
  empty: string
  t: C100Tokens
  children: ReactNode[]
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: t.muted,
          mb: 0.75
        }}
      >
        {title}
      </Typography>
      {children.length === 0 ? (
        <Typography sx={{ fontFamily: MONO, fontSize: 12, color: t.muted }}>{empty}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{children}</Box>
      )}
    </Box>
  )
}

function TickerChip({ ticker, name, meta, color, onClick, t }: {
  ticker: string
  name: string
  meta: string
  color: string
  onClick: (ticker: string) => void
  t: C100Tokens
}) {
  return (
    <Box
      component="button"
      onClick={() => onClick(ticker)}
      title={name}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        bgcolor: 'transparent',
        border: `1px solid ${t.hairline}`,
        borderRadius: 1.5,
        px: 1,
        py: 0.5,
        transition: 'background-color .15s, border-color .15s',
        '&:hover': { bgcolor: t.faintAccentBg, borderColor: t.accent },
        '&:focus-visible': { outline: `2px solid ${t.accent}`, outlineOffset: 1 }
      }}
    >
      <Typography sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, color: t.text, flexShrink: 0 }}>
        {ticker}
      </Typography>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 11,
          color: t.muted,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {name}
      </Typography>
      <Typography
        sx={{ fontFamily: MONO, fontSize: 11, color, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
      >
        {meta}
      </Typography>
    </Box>
  )
}
